/**
 * Draws the layout: tiled tab groups with resizable dividers, floating windows above them,
 * and the dock compass shown while a window is dragged.
 *
 * - Drag a **tab** (or a floating window's **title bar**). A compass appears over the tab
 *   group under the pointer: drop on its centre to add the window as a tab, on a side to split
 *   the group. Dropping a tab anywhere else makes it float.
 * - Drag a **divider** to resize, drag a floating window's corner to resize it.
 * - Every window renders inside its own error boundary: if it crashes, only that window shows
 *   an error card (and the crash is written to the core's log); the rest keeps working.
 *
 * On a narrow screen (a phone) the workspace shows one window at a time instead
 * (`CompactView`), with the same windows and without changing the layout.
 *
 * Files: `AreaViews` (splits, tabs), `FloatingView`, `WindowBody` (error boundary),
 * `DropOverlay` (compass), `CompactView`, `geometry.ts`, `areaContext.ts`. This file owns the drag.
 */
import { For, Show, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { useApp } from "../app/context";
import * as L from "./layout";
import type { AreaContext } from "./areaContext";
import { AreaView } from "./AreaViews";
import { CompactView } from "./CompactView";
import { DropOverlay } from "./DropOverlay";
import { FloatingView } from "./FloatingView";
import { ZONES, clamp, type Drag, type Target } from "./geometry";
import { COMPACT_WIDTH, COMPASS } from "./constants";

/** The whole window area. */
export function Workspace(): JSX.Element {
  const { wm } = useApp();
  let root!: HTMLDivElement;
  const groups = new Map<string, HTMLElement>();
  const [drag, setDrag] = createSignal<Drag | null>(null);
  /** `true` while the workspace is narrower than `COMPACT_WIDTH` (one window at a time). */
  const [compact, setCompact] = createSignal(false);
  onMount(() => {
    const measure = () => setCompact(root.clientWidth > 0 && root.clientWidth < COMPACT_WIDTH);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    onCleanup(() => observer.disconnect());
  });

  /** The group and compass square under a screen point, ignoring a drag onto the window's own lone tab. */
  function targetAt(clientX: number, clientY: number, dragged: string): Target | null {
    const ws = root.getBoundingClientRect();
    for (const [areaId, el] of groups) {
      const r = el.getBoundingClientRect();
      if (clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) continue;
      const area = L.findArea(wm.layout().root, areaId);
      if (area?.kind === "tabs" && area.windows.length === 1 && area.windows[0] === dragged) return null;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const hit = ZONES.find(([, dx, dy]) => Math.abs(clientX - (cx + dx * COMPASS.step)) <= COMPASS.size / 2 && Math.abs(clientY - (cy + dy * COMPASS.step)) <= COMPASS.size / 2);
      return { areaId, zone: hit?.[0] ?? null, rect: { x: r.left - ws.left, y: r.top - ws.top, w: r.width, h: r.height } };
    }
    return null;
  }

  /** Starts dragging a window from a tab or a floating title bar. */
  function beginDrag(event: PointerEvent, win: string, source: "tab" | "floating"): void {
    if (event.button !== 0) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const startRect = wm.layout().floating.find((f) => f.window === win);
    let moving = false;

    const move = (ev: PointerEvent) => {
      if (!moving && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 5) return;
      moving = true;
      if (source === "floating" && startRect) {
        const ws = root.getBoundingClientRect();
        wm.update((l) =>
          L.moveFloating(l, win, {
            x: clamp(startRect.x + ev.clientX - startX, -startRect.width + 80, ws.width - 80),
            y: clamp(startRect.y + ev.clientY - startY, 0, ws.height - 32),
          }),
        );
      }
      setDrag({ window: win, target: targetAt(ev.clientX, ev.clientY, win) });
    };
    const up = (ev: PointerEvent) => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", up);
      removeEventListener("pointercancel", up);
      const target = drag()?.target;
      setDrag(null);
      if (!moving || ev.type === "pointercancel") return;
      if (target?.zone) {
        wm.update((l) => L.dock(l, win, target.areaId, target.zone!));
      } else if (source === "tab") {
        const ws = root.getBoundingClientRect();
        wm.float(win, {
          x: clamp(ev.clientX - ws.left - 80, 0, Math.max(0, ws.width - 380)),
          y: clamp(ev.clientY - ws.top - 14, 0, Math.max(0, ws.height - 300)),
          width: 380,
          height: 300,
        });
      }
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", up);
    addEventListener("pointercancel", up);
  }

  const context: AreaContext = {
    beginDrag,
    register(id, el) {
      groups.set(id, el);
      onCleanup(() => groups.get(id) === el && groups.delete(id));
    },
  };

  const rootId = createMemo(() => wm.layout().root?.id ?? null);
  const floatingIds = createMemo(() => wm.layout().floating.map((f) => f.window));

  return (
    <div class="workspace" ref={root} classList={{ dragging: !!drag() }}>
      <Show
        when={!compact()}
        fallback={
          <div class="tiles">
            <CompactView />
          </div>
        }
      >
        <div class="tiles">
          <Show when={rootId()} keyed fallback={<div class="empty-tiles">No window is docked. Open one from the Windows menu, or drag a floating window here.</div>}>
            {(id) => <AreaView id={id} ctx={context} />}
          </Show>
        </div>
        <For each={floatingIds()}>{(win) => <FloatingView window={win} ctx={context} />}</For>
        <Show when={drag()?.target}>{(target) => <DropOverlay target={target()} />}</Show>
      </Show>
    </div>
  );
}
